import React, { useState } from 'react';
import { Modal } from '../modals';
import { Button } from '../ui';

interface PasswordDisplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  password: string;
}

export const PasswordDisplayModal: React.FC<PasswordDisplayModalProps> = ({
  isOpen,
  onClose,
  username,
  password,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="User Created Successfully"
      size="sm"
      footer={
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={handleClose}
          >
            Done
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium text-green-700 dark:text-green-300">
              User "{username}" has been created
            </span>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start mb-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-amber-700 dark:text-amber-300 mb-1">
                Save this password now
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                This password will only be shown once. Please save it securely.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-md font-mono text-sm text-gray-900 dark:text-gray-100">
              {password}
            </code>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCopy}
              className="shrink-0"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          The user will be required to change this password on their first login.
        </p>
      </div>
    </Modal>
  );
};
